import $ from 'jquery';
import { IS_ACTIVE_CLASS } from '../constants/classes';
import { IS_FOCUS_ACTIVE_CLASS } from '../constants/classes';

/**
 * An object of selectors used in this view
 * @property SELECTORS
 * @type {Object}
 * @final
 */
const SELECTORS = {
    TOGGLE_TRIGGER: '.js-toggleView-trigger',
    FOCUS_TARGET: '.js-toggleView-focusTarget'
};


/**
 * On event, it finds the toggle target and toggles an active class.
 *
 * @class ToggleView
 * @param {jQuery} [$trigger] Pass in containing dom element
 * @constructor
 */
export default class ToggleView {
    constructor($element, options) {
        if (!$element) {
            throw ('Required $element configuration object not passed to ToggleView constructor');
        }

        if (!$element instanceof $) { // jshint ignore:line
            throw ('Required $element object passed to ToggleView constructor is the wrong type');
        }
        // If view element isn't on the page
        if (!$element.length) {
            return;
        }
        /**
         * A reference to the containing DOM element
         *
         * @property $element
         * @type {jQuery}
         * @public
         */
        this.$element = $element;

        /**
         * Tracks whether the view is enabled
         *
         * @default false
         * @property isEnabled
         * @type {Boolean}
         * @public
         */
        this.isEnabled = false;
        this.init();

    };

    /**
     * Initializes the UI Component View
     *
     * @method init
     * @public
     * @chainable
     */
    init() {
        this.setupHandlers()
            .createChildren()
            .enable();
        return this;
    };
    /**
     * Binds the scope of any handler functions
     * Should only be run on initialization of the view
     *
     * @method setupHandlers
     * @public
     * @chainable
     */
    setupHandlers() {
        this.onToggleMouseDownHandler = this.onToggleMouseDown.bind(this);
        this.onDocumentClickHandler = this.onDocumentClick.bind(this);
        this.onChildFocusHandler = this.onChildFocus.bind(this);
        this.onChildFocusOutHandler = this.onChildFocusOut.bind(this);

        return this;
    };
    /**
     * Create any child objects or references to DOM elements
     * Should only be run on initialization of the view
     *
     * @method createChildren
     * @public
     * @chainable
     */
    createChildren() {
        this.$toggleTrigger = this.$element.find(SELECTORS.TOGGLE_TRIGGER);
        this.$toggleTarget = this.determineTarget();
        this.$focusTarget = this.$element.find(SELECTORS.FOCUS_TARGET);

        if (this.$focusTarget.length) {
            this.$focusableChildren = this.$focusTarget.find('a, button, :input, [tabindex], select');
        }

        return this;
    };
    /**
     * Determine Target
     *
     * @method determineTarget
     * @public
     * @chainable
     */
    determineTarget() {
        if (this.$toggleTrigger.data('toggleTarget')) {
            return this.$toggleTarget = this.$element.find("." + this.$toggleTrigger.data('toggleTarget'));
        } else {
            return this.$toggleTarget = this.$element;
        }
    }

    /**
     * Clears active class when user clicks
     * off the parent element.
     *
     * @method onDocumentClickHandler
     * @public
     * @chainable
     */
    onDocumentClick(e) {
        if (this.$element.find(e.target).length === 0) {
            this.$toggleTarget.removeClass(IS_ACTIVE_CLASS);
            this.$toggleTrigger.attr('aria-expanded', 'false');
            this.$element.find('[aria-hidden]').attr('aria-hidden', 'true');
        }

        return this;
    }

    /**
     * Handles adding focus active class
     * on focusTarget when focusTarget children have focus
     *
     * @method onChildFocus
     * @public
     * @chainable
     */
    onChildFocus() {
        this.$toggleTarget.addClass(IS_FOCUS_ACTIVE_CLASS);
        if (this.$toggleTrigger.attr('aria-expanded') === 'false') {
            this.$toggleTrigger.attr('aria-expanded', 'true');
            this.$element.find('[aria-hidden]').attr('aria-hidden', 'false');
        }

        return this;
    }

    /**
     * Handles toggling focus active class
     * on focusTarget when focusTarget children have focus
     *
     * @method onChildFocusOut
     * @public
     * @chainable
     */
    onChildFocusOut() {
        this.$toggleTarget.removeClass(IS_FOCUS_ACTIVE_CLASS);
        if (this.$toggleTrigger.attr('aria-expanded') === 'true' && !this.$element.hasClass(IS_ACTIVE_CLASS)) {
            this.$toggleTrigger.attr('aria-expanded', 'false');
            this.$element.find('[aria-hidden]').attr('aria-hidden', 'true');
        }

        return this;
    }

    /**
     * Enables the component
     * Performs any event binding to handlers
     *
     * @method enable
     * @public
     * @chainable
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.$toggleTrigger.on('click', this.onToggleMouseDownHandler);

        if (this.$element.data('toggleOffClick')) {
            $(document).on('click touchstart', this.onDocumentClickHandler);
        }

        if (this.$focusTarget.length) {
            this.$focusableChildren.on('focus', this.onChildFocusHandler);
            this.$focusableChildren.on('focusout', this.onChildFocusOutHandler);
        }

        this.isEnabled = true;
        return this;
    };
    /**
     * Disables the component
     * Tears down any event binding to handlers
     *
     * @method disable
     * @public
     * @chainable
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        this.$toggleTrigger.off('click', this.onToggleMouseDownHandler);
        this.isEnabled = false;
        return this;
    };


    /**
     * Toggles the active class on either the element
     * that was clicked on, or if specified, a separate
     * element specified by the "data-toggle-target"
     * attribute.
     * 
     * @method onToggleMouseDown
     * @public
     * @param {Object}
     */

    onToggleMouseDown() {
        this.$toggleTarget.toggleClass(IS_ACTIVE_CLASS);
        if (this.$toggleTrigger.attr('aria-expanded') === 'true' && !this.$element.hasClass(IS_FOCUS_ACTIVE_CLASS)) {
            this.$toggleTrigger.attr('aria-expanded', 'false');
            this.$element.find('[aria-hidden]').attr('aria-hidden', 'true');
        } else if (this.$toggleTrigger.attr('aria-expanded') === 'false') {
            this.$toggleTrigger.attr('aria-expanded', 'true');
            this.$element.find('[aria-hidden]').attr('aria-hidden', 'false');
        } else {}

        /* Adds focus to an ID value provided by this data attribute */
        if (this.$toggleTrigger.data('focusTo')) {
            const focusToElement = document.getElementById(this.$toggleTrigger.data('focusTo'));
            focusToElement.focus();
        }

        return this;
    }

}